import React, { FC, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import addValidationSchema, { AddRoleFormData } from "./validation.schema";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { a11yProps } from "@/utils/a11yProps";
import TabPanel from "@/components/TabPanel";
import { TabRoleGeneral } from "./TabRoleGeneral";
import { TabRoleActions } from "./TabRoleActions";
import { RoleStatusEnum, RoleTypeEnum } from "@/domain/role.enum";
import { useCreateRole } from "@/hooks/management/useRoles.manage";

type RoleAddModalModalProps = {
  open: boolean;
  onClose: () => void;
};

const RoleAddModal: FC<RoleAddModalModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation("roles");
  const { t: tCommon } = useTranslation("common");
  const { userInfo, userHasAccessToCode } = useUserStore();
  const { showSnackBar, showSimpleAlert } = useFeedBack();
  const theme = useAppTheme();

  const createAuditLog = useCreateAuditlog();
  const createRole = useCreateRole();

  const [selectedTab, setSelectedTab] = useState(0);

  const methods = useForm<AddRoleFormData>({
    resolver: yupResolver(addValidationSchema(tCommon)),
    mode: "all",
    defaultValues: {
      name: "",
      status: RoleStatusEnum.ACTIVE,
      type: RoleTypeEnum.CUSTOM_ROLE,
      actions: [],
    },
  });

  const handleTabChange = async (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedTab(newValue);
  };

  const onSubmit = async (data: AddRoleFormData) => {
    const payload = {
      name: data.name as string,
      status: data.status as RoleStatusEnum,
      type: data.type as RoleTypeEnum,
      actions: data.actions as string[],
      createdBy: userInfo?._id,
    };

    await createRole.mutateAsync(payload, {
      onSuccess: () => {
        onClose();
        showSnackBar(t("messages.add-success"), "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ROLES,
          option: OPTIONS.ROLES_ADD,
          actionCode: ACTION_CODE.WEB_ROLES_ADD,
          action: "When adding a new roles(adding)",
          detail: `New role added ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      },
      onError: () => {
        showSnackBar(t("messages.add-error"), "error");
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={false}
      title={t("modals.title-add")}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 440,
        maxHeight: 900,
        maxWidth: 860,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", minHeight: 380 }}>
            <Box
              sx={{
                padding: "20px 0 0",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                aria-label="role tabs"
              >
                <Tab label={t("tabs.create-role")} {...a11yProps(0)} />
                <Tab label={t("tabs.assign-actions")} {...a11yProps(1)} />
              </Tabs>
            </Box>
            <TabPanel value={selectedTab} index={0}>
              <TabRoleGeneral />
            </TabPanel>
            <TabPanel value={selectedTab} index={1}>
              <TabRoleActions />
            </TabPanel>
          </Box>
        </FormProvider>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={false}
            label={t("buttons.cancel")}
          />
          <Button
            variantType="save"
            onClick={async () => {
              const isValid = await methods.trigger();
              if (isValid) {
                methods.handleSubmit(onSubmit)();
              } else {
                showSnackBar(t("messages.form-with-errors"));
              }
            }}
            disabled={createRole.isPending}
            isLoading={createRole.isPending}
            label={t("buttons.create")}
          />
        </Box>
      }
    />
  );
};

export default RoleAddModal;
